import { FC, useRef, useState } from 'react';
import { Controller, Form, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { useAssistant } from '@/entities/assistant/hooks/useAssistant';
import { privateDocumentsCategoryI18nMap } from '@/entities/document';
import { categoryList } from '@/entities/privateDocuments';
import { Country } from '@/shared/components';
import { CountriesList } from '@/shared/dictionaries/countries';
import { FileUploadingRef } from '@/shared/lib/hooks/useFormRenderV2/components/FileUploading';
import { PrivateDocumentCategory, PrivateDocumentDto, useSJDatabase } from '@/shared/lib/sj-orm';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import { CollectionName } from '@/shared/lib/sj-orm/types';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { useProfileStore } from '@/shared/store/profile.store';
import { CountryCode } from '@/shared/types';
import { Title } from '@/shared/uikit/Title';
import { Button } from '@/shared/uikitV2/Button';
import { Input } from '@/shared/uikitV2/Input';
import { Select } from '@/shared/uikitV2/Select';
import { FileUploader, UploadState } from '@/widgets/FileUploader';

import { BtnClose } from '../../components/BtnClose';
import { SelectOptions } from '../../components/SelectItem/types';

import { Body, Container, Footer, Header, StyledDrawer, StyledTitle } from './styled';

type CreateDocumentProps = {
  onCreate?: (data: SelectOptions) => void;
  setOpen: (open: boolean) => void;
  privateCategory?: PrivateDocumentCategory;
};

type FormFields = {
  name: string;
  privateCategory: PrivateDocumentCategory;
  country: CountryCode;
};

export const CreateDocument: FC<CreateDocumentProps> = ({ onCreate, setOpen, privateCategory }) => {
  const { t: ts } = useTranslation('widgets/modals', { keyPrefix: 'selectEntities' });
  const { t: te } = useTranslation('entities/privateDocuments', { keyPrefix: 'category' });
  const { keyPair } = useKeyPairStore();
  const sjDb = useSJDatabase();
  const uploaderRef = useRef<FileUploadingRef>(null);
  const [uploadState, setUploadState] = useState<UploadState | null>();
  const { delegation } = useAssistant();
  const { isAssistant } = useProfileStore();

  const { control, handleSubmit: formHandleSubmit } = useForm<FormFields>({
    defaultValues: {
      privateCategory,
    },
  });

  const onSubmit = async (data: FormFields) => {
    if (!keyPair || !uploaderRef.current) return;
    const res = await uploaderRef.current.upload(true);

    const documentDto: PrivateDocumentDto = {
      id: sjDb.generateId(CollectionName.PRIVATE_DOCUMENTS),
      type: DocumentType.PRIVATE_DOCUMENTS,
      ...data,
    };

    if (res.success) {
      documentDto.attachments = res.filePaths;
    }

    const publicKeys = [keyPair.publicKey];

    if (delegation?.ownerPublicKey && isAssistant) {
      publicKeys.push(delegation.ownerPublicKey);
      documentDto.isCreatedByAssistant = true;
    }

    const document = await sjDb.collections.privateDocuments!.create(documentDto, publicKeys);

    if (onCreate) {
      onCreate({ colName: CollectionName.PRIVATE_DOCUMENTS, id: document!.id });
    }

    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Form control={control} onSubmit={({ data }) => onSubmit(data)}>
      <StyledDrawer width={400} footer={null} closable={false} onClose={handleClose} open>
        <Container>
          <Header>
            <BtnClose onClick={handleClose} style={{ position: 'absolute', right: 24, top: 24 }} />
            <StyledTitle className="truncate" size={22} bold>
              {ts('titleAddDocument')}
            </StyledTitle>
          </Header>
          <Body>
            <Controller
              name="name"
              control={control}
              rules={{ required: ts('createDocument.mandatoryField') }}
              render={({ field, fieldState }) => (
                <Input
                  label={ts('createDocument.name')}
                  type="text"
                  required
                  status={fieldState.error ? 'error' : 'default'}
                  errorText={fieldState.error?.message}
                  {...field}
                />
              )}
            />
            {/* TODO: move to entities/SelectPrivateCategoryV2 */}
            <Controller
              name="privateCategory"
              control={control}
              rules={{ required: ts('createDocument.mandatoryField') }}
              render={({ field, fieldState }) => (
                <Select
                  label={ts('createDocument.category')}
                  options={categoryList.map((category) => ({
                    value: category,
                    content: { label: te(privateDocumentsCategoryI18nMap[category]) },
                  }))}
                  status={fieldState.error ? 'error' : 'default'}
                  errorText={fieldState.error?.message}
                  required
                  {...field}
                  value={field.value ?? ''}
                  disabled={Boolean(privateCategory)}
                />
              )}
            />
            {/* TODO: move to entities/SelectCountryV2 */}
            <Controller
              name="country"
              control={control}
              rules={{ required: ts('createDocument.mandatoryField') }}
              render={({ field, fieldState }) => (
                <Select
                  label={ts('createDocument.country')}
                  status={fieldState.error ? 'error' : 'default'}
                  errorText={fieldState.error?.message}
                  options={CountriesList.map((country) => ({
                    value: country.key,
                    content: { label: country.label },
                  }))}
                  required
                  iconAfter={field.value ? <Country code={field.value} icon /> : null}
                  {...field}
                  value={field.value ?? ''}
                />
              )}
            />
            <Title size={16} bold>
              {ts('createDocument.scan')}
            </Title>
            <FileUploader onUpdate={setUploadState} ref={uploaderRef} />
          </Body>
          <Footer>
            <Button type="text" htmlType="button" onClick={handleClose}>
              {ts('btnCancel')}
            </Button>
            <Button onClick={formHandleSubmit(onSubmit)} loading={uploadState?.uploading}>
              {ts('btnConfirm')}
            </Button>
          </Footer>
        </Container>
      </StyledDrawer>
    </Form>
  );
};
